import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { formatCalendarOption, toDateInputValue } from '../utils/laborCalendarUtils';
import {
  PATTERN_TEMPLATES,
  PATTERN_TEMPLATE_IDS,
  buildPatternDaysForTemplate,
  defaultCycleStartForYear,
  describePatternDays,
  getPatternTemplate,
  suggestPatternCode,
} from '../utils/laborPatternTemplates';
import {
  CREATION_MODE,
  MANUAL_PATTERN_TYPE,
  buildPatternDaysFromManualRows,
  defaultFixedManualRows,
  defaultRotatingManualRows,
  describeManualPatternDays,
  patternDaysToManualRows,
  resizeRotatingManualRows,
  validateManualPatternRows,
} from '../utils/laborPatternManual';
import { LaborPatternManualDayTable } from './LaborPatternManualDayTable';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

export function LaborCreatePatternModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  patternId = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const isEditMode = patternId != null && patternId !== '';
  const [creationMode, setCreationMode] = useState(CREATION_MODE.TEMPLATE);
  const [templateId, setTemplateId] = useState(PATTERN_TEMPLATE_IDS.ROTATIVE_3X8);
  const [manualType, setManualType] = useState(MANUAL_PATTERN_TYPE.FIXED);
  const [manualCycleDays, setManualCycleDays] = useState(7);
  const [manualRows, setManualRows] = useState(defaultFixedManualRows);
  const [calendarId, setCalendarId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [cycleStart, setCycleStart] = useState('');
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [formLoaded, setFormLoaded] = useState(false);

  const {
    data: existingPattern,
    isLoading: loadingPattern,
    isError: patternLoadError,
    error: patternLoadErrorDetail,
  } = useQuery({
    queryKey: ['labor-pattern', patternId],
    queryFn: () => laborScheduleService.getPattern(Number(patternId)),
    enabled: isOpen && isEditMode,
  });

  const template = getPatternTemplate(templateId);
  const isManual = isEditMode || creationMode === CREATION_MODE.MANUAL;
  const isManualRotating = isManual && manualType === MANUAL_PATTERN_TYPE.ROTATING;
  const isRotating = isManual ? isManualRotating : template.isRotating;
  const effectiveCycleDays = isManual
    ? isManualRotating
      ? manualCycleDays
      : 0
    : template.cycleDays;

  const previewDays = useMemo(() => {
    if (isManual) {
      return buildPatternDaysFromManualRows(manualType, manualRows);
    }
    return buildPatternDaysForTemplate(templateId);
  }, [isManual, manualType, manualRows, templateId]);

  const previewDescription = useMemo(() => {
    if (isManual) {
      return describeManualPatternDays(manualType, manualRows, manualCycleDays);
    }
    return describePatternDays(templateId, previewDays);
  }, [isManual, manualType, manualRows, manualCycleDays, templateId, previewDays]);

  const {
    data: calendars = [],
    isLoading: loadingCalendars,
    isError: listError,
    error: listErrorDetail,
  } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      setFormLoaded(false);
      setError(null);
      setProgress(null);
      return;
    }
    setCreationMode(CREATION_MODE.TEMPLATE);
    setManualType(MANUAL_PATTERN_TYPE.FIXED);
    setManualCycleDays(7);
    setManualRows(defaultFixedManualRows());
    const tpl = getPatternTemplate(PATTERN_TEMPLATE_IDS.ROTATIVE_3X8);
    setTemplateId(PATTERN_TEMPLATE_IDS.ROTATIVE_3X8);
    setName(tpl.defaultName);
    setCode(suggestPatternCode(tpl.defaultCodePrefix));
    setDescription('');
    setActive(true);
    setCycleStart(defaultCycleStartForYear(defaultYear));
    setError(null);
    setProgress(null);
    setFormLoaded(true);
  }, [isOpen, defaultYear, isEditMode]);

  useEffect(() => {
    if (!isOpen || !isEditMode || !existingPattern) return;
    const { manualType: mt, manualCycleDays: cd, manualRows: rows } = patternDaysToManualRows(
      existingPattern,
      existingPattern.pattern_days
    );
    setCreationMode(CREATION_MODE.MANUAL);
    setManualType(mt);
    setManualCycleDays(cd);
    setManualRows(rows);
    setCalendarId(String(existingPattern.calendar_id));
    setName(existingPattern.name || '');
    setCode(existingPattern.code || '');
    setDescription(existingPattern.description || '');
    setActive(existingPattern.active !== false);
    setCycleStart(
      existingPattern.cycle_start_date
        ? toDateInputValue(existingPattern.cycle_start_date)
        : defaultCycleStartForYear(defaultYear)
    );
    setFormLoaded(true);
  }, [isOpen, isEditMode, existingPattern, defaultYear]);

  useEffect(() => {
    if (!isOpen || isEditMode || creationMode !== CREATION_MODE.TEMPLATE) return;
    const tpl = getPatternTemplate(templateId);
    setName(tpl.defaultName);
    setCode(suggestPatternCode(tpl.defaultCodePrefix));
  }, [templateId, isOpen, creationMode]);

  useEffect(() => {
    if (!isOpen || isEditMode || creationMode !== CREATION_MODE.MANUAL) return;
    if (manualType === MANUAL_PATTERN_TYPE.FIXED) {
      setManualRows(defaultFixedManualRows());
      if (!name.trim()) {
        setName('Patrón personalizado');
        setCode(suggestPatternCode('CUSTOM'));
      }
    } else {
      setManualRows(defaultRotatingManualRows(manualCycleDays));
      if (!name.trim()) {
        setName('Patrón rotativo personalizado');
        setCode(suggestPatternCode('ROT-CUSTOM'));
      }
    }
  }, [creationMode, manualType, isOpen]);

  useEffect(() => {
    if (!isOpen || isEditMode || creationMode !== CREATION_MODE.MANUAL) return;
    if (manualType !== MANUAL_PATTERN_TYPE.ROTATING) return;
    setManualRows((prev) => resizeRotatingManualRows(prev, manualCycleDays));
  }, [manualCycleDays, isOpen, creationMode, manualType]);

  useEffect(() => {
    if (!isOpen || isEditMode || calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, calendars, defaultYear]);

  const mutation = useMutation({
    mutationFn: async () => {
      const calId = Number(calendarId);
      if (!calId && !isEditMode) throw new Error('Selecciona un calendario de planta.');
      if (!name.trim()) throw new Error('Indica un nombre para el patrón.');
      if (!isEditMode && !code.trim()) throw new Error('Indica un código único para el patrón.');

      let days;
      let patternMeta;

      if (isEditMode || isManual) {
        const validationError = validateManualPatternRows(manualType, manualRows, {
          cycleDays: manualCycleDays,
          cycleStart,
        });
        if (validationError) throw new Error(validationError);

        days = buildPatternDaysFromManualRows(manualType, manualRows);
        if (days.length === 0) throw new Error('No hay días laborables definidos.');

        if (isEditMode) {
          return laborScheduleService.updatePattern(Number(patternId), {
            name: name.trim(),
            description: description.trim(),
            active,
            cycleStartDate: manualType === MANUAL_PATTERN_TYPE.ROTATING ? cycleStart : null,
            days,
          });
        }

        patternMeta = {
          calendarId: calId,
          name: name.trim(),
          code: code.trim(),
          description: isManualRotating
            ? `Patrón rotativo manual (${manualCycleDays} días de ciclo)`
            : 'Patrón fijo manual (día de semana)',
          isRotating: isManualRotating,
          cycleDays: isManualRotating ? manualCycleDays : 0,
          cycleStartDate: isManualRotating ? cycleStart : null,
        };
      } else {
        days = buildPatternDaysForTemplate(templateId);
        if (days.length === 0) throw new Error('Plantilla sin días definidos.');

        patternMeta = {
          calendarId: calId,
          name: name.trim(),
          code: code.trim(),
          description: template.description,
          isRotating: template.isRotating,
          cycleDays: template.cycleDays,
          cycleStartDate: template.isRotating ? cycleStart : null,
        };
      }

      return laborScheduleService.createPatternWithDays(
        { pattern: patternMeta, days },
        (p) => setProgress(p)
      );
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['labor-calendars', factoryId] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-patterns-by-calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      if (isEditMode) {
        await queryClient.invalidateQueries({ queryKey: ['labor-pattern', patternId] });
      }
      onSuccess?.(result);
      onClose();
    },
    onError: (err) => {
      setProgress(null);
      setError(
        err?.message || (isEditMode ? 'No se pudo actualizar el patrón' : 'No se pudo crear el patrón')
      );
    },
  });

  const isFormReady = !isEditMode || (formLoaded && !loadingPattern);
  const isPending = mutation.isPending;

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          setProgress(null);
          mutation.mutate();
        }}
        disabled={
          isPending ||
          !isFormReady ||
          loadingCalendars ||
          (!isEditMode && calendars.length === 0) ||
          (!isEditMode && !calendarId)
        }
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {isEditMode ? 'Guardar cambios' : 'Crear patrón y días'}
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Editar patrón de trabajo' : 'Crear patrón de trabajo'}
      icon={Layers}
      footer={footer}
      size="lg"
    >
      {isEditMode && loadingPattern && (
        <p className="text-gray-500 text-sm mb-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando patrón…
        </p>
      )}
      {isEditMode && patternLoadError && (
        <p className="text-red-400 text-sm mb-4">
          {patternLoadErrorDetail?.message || 'Error al cargar el patrón'}
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {progress && (
        <div className="mb-4 p-3 rounded-lg border border-blue-800/50 bg-blue-900/20 text-sm text-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            <span>
              Añadiendo días {progress.current}/{progress.total}: {progress.label}
            </span>
          </div>
          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Fábrica del header: <span className="text-white">{factoryCode || '—'}</span>.
        {isEditMode
          ? ' Modifica nombre, horarios y estado activo. El código y el tipo de patrón no se pueden cambiar.'
          : ' El patrón se asocia al calendario laboral del año seleccionado.'}
      </p>

      {!isEditMode && (
        <>
          <label className="block text-sm text-gray-300 mb-2">Origen del patrón</label>
          <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-4">
            <button
              type="button"
              onClick={() => setCreationMode(CREATION_MODE.TEMPLATE)}
              disabled={isPending}
              className={`flex-1 px-3 py-2 text-sm ${
                creationMode === CREATION_MODE.TEMPLATE
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
              }`}
            >
              Plantilla
            </button>
            <button
              type="button"
              onClick={() => setCreationMode(CREATION_MODE.MANUAL)}
              disabled={isPending}
              className={`flex-1 px-3 py-2 text-sm ${
                creationMode === CREATION_MODE.MANUAL
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
              }`}
            >
              A mano
            </button>
          </div>
        </>
      )}

      {!isEditMode && creationMode === CREATION_MODE.TEMPLATE ? (
        <>
          <label htmlFor="labor-pattern-template" className="block text-sm text-gray-300 mb-2">
            Plantilla <span className="text-red-400">*</span>
          </label>
          <select
            id="labor-pattern-template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={isPending}
            className="w-full mb-2 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            {PATTERN_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mb-4">{template.description}</p>
        </>
      ) : (isEditMode || creationMode === CREATION_MODE.MANUAL) ? (
        <>
          {!isEditMode && (
            <>
              <label className="block text-sm text-gray-300 mb-2">Tipo manual</label>
              <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => setManualType(MANUAL_PATTERN_TYPE.FIXED)}
                  disabled={isPending}
                  className={`flex-1 px-3 py-2 text-sm ${
                    manualType === MANUAL_PATTERN_TYPE.FIXED
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  Fijo (L–D)
                </button>
                <button
                  type="button"
                  onClick={() => setManualType(MANUAL_PATTERN_TYPE.ROTATING)}
                  disabled={isPending}
                  className={`flex-1 px-3 py-2 text-sm ${
                    manualType === MANUAL_PATTERN_TYPE.ROTATING
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  Rotativo
                </button>
              </div>
            </>
          )}

          {isEditMode && (
            <p className="text-xs text-gray-500 mb-4">
              Tipo: {manualType === MANUAL_PATTERN_TYPE.ROTATING ? 'Rotativo' : 'Fijo'}
              {manualType === MANUAL_PATTERN_TYPE.ROTATING
                ? ` · ciclo de ${manualCycleDays} días`
                : ''}
            </p>
          )}

          {!isEditMode && isManualRotating && (
            <div className="mb-4">
              <label htmlFor="labor-manual-cycle-days" className="block text-sm text-gray-300 mb-2">
                Días del ciclo <span className="text-red-400">*</span>
              </label>
              <input
                id="labor-manual-cycle-days"
                type="number"
                min={1}
                max={60}
                value={manualCycleDays}
                onChange={(e) => setManualCycleDays(Number(e.target.value) || 1)}
                disabled={isPending}
                className="w-32 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Entre 1 y 60. Marca qué secuencias son laborables.</p>
            </div>
          )}

          <p className="text-sm text-gray-300 mb-2">Días y horarios</p>
          <LaborPatternManualDayTable
            rows={manualRows}
            onChange={setManualRows}
            disabled={isPending || !isFormReady}
          />
          <p className="text-xs text-gray-500 mt-2 mb-4">
            Marca los días laborables y define horario. Los no marcados serán descanso.
          </p>
        </>
      ) : null}

      {!isEditMode && loadingCalendars && (
        <p className="text-gray-500 text-sm mb-4">Cargando calendarios…</p>
      )}
      {!isEditMode && listError && (
        <p className="text-red-400 text-sm mb-4">
          {listErrorDetail?.message || 'Error al cargar calendarios'}
        </p>
      )}
      {!isEditMode && !loadingCalendars && calendars.length === 0 && (
        <p className="text-amber-400 text-sm mb-4">
          No hay calendario de planta. Créalo en la vista Fábrica antes de añadir patrones.
        </p>
      )}

      {!isEditMode && calendars.length > 0 && (
        <>
          <label htmlFor="labor-pattern-calendar" className="block text-sm text-gray-300 mb-2">
            Calendario de planta <span className="text-red-400">*</span>
          </label>
          <select
            id="labor-pattern-calendar"
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            disabled={isPending}
            className="w-full mb-4 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            {calendars.map((cal) => (
              <option key={cal.id} value={cal.id}>
                {formatCalendarOption(cal)}
              </option>
            ))}
          </select>
        </>
      )}

      {isEditMode && calendarId && (
        <p className="text-xs text-gray-500 mb-4">
          Calendario ID {calendarId} (no editable)
        </p>
      )}

      <label htmlFor="labor-pattern-name" className="block text-sm text-gray-300 mb-2">
        Nombre <span className="text-red-400">*</span>
      </label>
      <input
        id="labor-pattern-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={isPending || !isFormReady}
        className="w-full mb-4 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
      />

      {isEditMode && (
        <>
          <label htmlFor="labor-pattern-description" className="block text-sm text-gray-300 mb-2">
            Descripción
          </label>
          <textarea
            id="labor-pattern-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isPending || !isFormReady}
            rows={2}
            className="w-full mb-4 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-y"
          />
          <label className="flex items-center gap-2 text-sm text-gray-300 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={isPending || !isFormReady}
              className="rounded border-gray-600"
            />
            Patrón activo
          </label>
        </>
      )}

      <label htmlFor="labor-pattern-code" className="block text-sm text-gray-300 mb-2">
        Código (único) {!isEditMode && <span className="text-red-400">*</span>}
      </label>
      <input
        id="labor-pattern-code"
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={isPending || isEditMode || !isFormReady}
        readOnly={isEditMode}
        className="w-full mb-4 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-70"
      />

      {isRotating && (
        <>
          <label htmlFor="labor-pattern-cycle-start" className="block text-sm text-gray-300 mb-2">
            Inicio del ciclo (día 0) <span className="text-red-400">*</span>
          </label>
          <input
            id="labor-pattern-cycle-start"
            type="date"
            value={cycleStart}
            onChange={(e) => setCycleStart(e.target.value)}
            disabled={isPending || !isFormReady}
            className="w-full mb-2 bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-500 mb-4">
            Ciclo de {effectiveCycleDays} días. Recomendado: primer lunes del año {defaultYear}.
          </p>
        </>
      )}

      {isFormReady && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-3 text-sm">
          <div className="flex items-start gap-2 text-gray-300">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-white mb-1">Vista previa</p>
              <p className="text-gray-400 text-xs mb-1">
                {isRotating ? 'Rotativo' : 'Fijo por día de semana'}
                {effectiveCycleDays ? ` · ciclo ${effectiveCycleDays} días` : ''}
              </p>
              <p className="text-gray-300">{previewDescription}</p>
              <p className="text-gray-500 text-xs mt-2">
                {isEditMode ? 'Se guardarán' : 'Se crearán'} {previewDays.length} día(s) laborable(s)
                en el patrón.
              </p>
            </div>
          </div>
        </div>
      )}
    </LaborCalendarModalShell>
  );
}
